import { useQuery } from "@apollo/client";
import React from "react";
import gql from "graphql-tag";
import Link from "next/link";
import cn from "classnames";
import { notNull } from "util/arrays";
import { memoize } from "lodash";
import {
  MarketQuery,
  MarketQuery_market,
  MarketQuery_market_submarkets,
  MarketQuery_market_submarkets_neighborhoods,
} from "./types/MarketQuery";
import styles from "./styles.module.scss";

export const MARKET_QUERY = gql`
  query MarketQuery($marketUuid: ID) {
    market(externalId: $marketUuid) {
      id: externalId
      name
      seoString
      submarkets {
        id: externalId
        name
        seoString
        neighborhoods {
          id: externalId
          name
          seoString
        }
      }
    }
  }
`;

const FooterLocation = ({
  currentAreaUuid,
  area,
}: {
  currentAreaUuid: string;
  area:
    | MarketQuery_market_submarkets
    | MarketQuery_market_submarkets_neighborhoods
    | MarketQuery_market;
}) => {
  if (currentAreaUuid === area.id) return <>{area.name}</>;
  return <Link href={`${area.seoString}/${area.id}`}>{area.name}</Link>;
};

const isOpen = memoize(
  (submarket: MarketQuery_market_submarkets, currentAreaUuid: string) =>
    submarket.neighborhoods
      .filter(notNull)
      .some(({ id }) => id === currentAreaUuid)
);

const SEOFooter = ({
  marketUuid,
  currentAreaUuid,
}: {
  marketUuid: string;
  currentAreaUuid: string;
}) => {
  const { data, loading, error } = useQuery<MarketQuery>(MARKET_QUERY, {
    variables: { marketUuid },
    fetchPolicy: "cache-only",
  });

  // this data is prefetched in /m/[...slug]#getServerSideProps. With cache-only loading should never be true.
  // this guard is a catch to prevent crashes if for some reason the prefetch is removed or broken.
  if (loading || error || !data?.market) return <></>;

  const { market } = data;
  return (
    <details className={cn(styles.section, "inset")} data-qa="seo-footer">
      <summary>
        See more office space for rent in{" "}
        <FooterLocation currentAreaUuid={currentAreaUuid} area={market} />
      </summary>
      {market.submarkets.filter(notNull).map((submarket) => (
        <details
          key={submarket.id}
          open={isOpen(submarket, currentAreaUuid)}
          className={cn({
            [styles.empty]: submarket.neighborhoods.length === 0,
          })}
        >
          <summary>
            <FooterLocation
              currentAreaUuid={currentAreaUuid}
              area={submarket}
            />
          </summary>
          {submarket.neighborhoods.filter(notNull).map((neighborhood) => (
            <p key={neighborhood.id}>
              <FooterLocation
                currentAreaUuid={currentAreaUuid}
                area={neighborhood}
              />
            </p>
          ))}
        </details>
      ))}
    </details>
  );
};

export default SEOFooter;
